import * as Sentry from '@sentry/react'
import { db } from './firebase'
import { doc, getDoc, setDoc, getDocs, getDocsFromServer, collection, query, where } from 'firebase/firestore'

const COLLECTION = 'productImages'
const MAX_IMAGES = 5

// Resize + compress image to base64 data URL
function compressToDataURL(file, maxWidth = 1000, quality = 0.85) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      let { width, height } = img
      if (width > maxWidth) {
        const scale = maxWidth / width
        width = maxWidth
        height = Math.round(height * scale)
      }
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, width, height)
      resolve(canvas.toDataURL('image/jpeg', quality))
    }
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Image load failed')) }
    img.src = url
  })
}

// Generate a tiny thumbnail from a base64 data URL (~3-8KB each)
function shrinkToThumbnail(dataURL, maxWidth = 150, quality = 0.3) {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => {
      let { width, height } = img
      if (width > maxWidth) {
        const scale = maxWidth / width
        width = maxWidth
        height = Math.round(height * scale)
      }
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')
      ctx.drawImage(img, 0, 0, width, height)
      resolve(canvas.toDataURL('image/jpeg', quality))
    }
    img.onerror = () => resolve(dataURL)
    img.src = dataURL
  })
}

// Build Firestore doc ID from business key + item ID
function docId(businessKey, itemId) {
  return `${businessKey}_${itemId}`
}

// ─── Upload ───

export async function uploadProductImage(businessKey, itemId, file, onProgress) {
  try {
    const existing = await getProductImages(businessKey, itemId)
    if (existing.length >= MAX_IMAGES) {
      throw new Error(`Maximum ${MAX_IMAGES} images per product`)
    }

    onProgress?.(10)
    const dataURL = await compressToDataURL(file, 1000, 0.85)
    onProgress?.(70)

    const imageEntry = { url: dataURL, uploadedAt: new Date().toISOString() }
    const id = docId(businessKey, itemId)
    const docRef = doc(db, COLLECTION, id)
    const snap = await getDoc(docRef)
    const images = snap.exists() ? (snap.data().images || []) : []
    images.push(imageEntry)
    await setDoc(docRef, { businessKey, itemId, images, updatedAt: new Date().toISOString() })
    invalidateThumbCache(businessKey)
    onProgress?.(100)

    return imageEntry
  } catch (err) {
    console.warn('[imageService] uploadProductImage failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'imageService', fn: 'uploadProductImage', businessKey, itemId } })
    throw err
  }
}

// ─── Delete ───

export async function deleteProductImage(businessKey, itemId, imageUrl) {
  try {
    const id = docId(businessKey, itemId)
    const docRef = doc(db, COLLECTION, id)
    const snap = await getDoc(docRef)
    if (!snap.exists()) return

    const data = snap.data()
    const images = (data.images || []).filter(img => img.url !== imageUrl)
    await setDoc(docRef, { ...data, images, updatedAt: new Date().toISOString() })
    invalidateThumbCache(businessKey)
  } catch (err) {
    console.warn('[imageService] deleteProductImage failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'imageService', fn: 'deleteProductImage', businessKey, itemId } })
    throw err
  }
}

// ─── Get images for a single product (used by admin) ───

export async function getProductImages(businessKey, itemId) {
  try {
    const id = docId(businessKey, itemId)
    const docRef = doc(db, COLLECTION, id)
    const snap = await getDoc(docRef)
    if (!snap.exists()) return []
    return snap.data().images || []
  } catch (err) {
    console.warn('[imageService] getProductImages failed:', err?.message)
    Sentry.captureException(err, { extra: { module: 'imageService', fn: 'getProductImages', businessKey, itemId } })
    return []
  }
}

// ─── Thumbnail cache (localStorage) ───
// Only stores tiny thumbnails (~3-8KB each), NOT full images

const THUMB_CACHE_KEY = 'desres_thumbs_'
const THUMB_CACHE_TTL = 7 * 24 * 60 * 60 * 1000 // 7 days

function getCachedThumbs(businessKey) {
  try {
    const raw = localStorage.getItem(THUMB_CACHE_KEY + businessKey)
    if (!raw) return null
    const { data, ts } = JSON.parse(raw)
    if (Date.now() - ts > THUMB_CACHE_TTL) {
      localStorage.removeItem(THUMB_CACHE_KEY + businessKey)
      return null
    }
    // data is array of [itemId, thumbUrls[]]
    return new Map(data)
  } catch { return null }
}

function setCachedThumbs(businessKey, map) {
  try {
    localStorage.setItem(THUMB_CACHE_KEY + businessKey, JSON.stringify({
      data: [...map.entries()],
      ts: Date.now()
    }))
  } catch { /* localStorage full */ }
}

function invalidateThumbCache(businessKey) {
  localStorage.removeItem(THUMB_CACHE_KEY + businessKey)
  // Also clear old cache key format
  localStorage.removeItem('desres_img_cache_' + businessKey)
}

// ─── Public API: get first image per product (fast, cached) ───

export async function getAllBusinessThumbnails(businessKey) {
  const cached = getCachedThumbs(businessKey)
  if (cached && cached.size > 0) {
    // Refresh in background — failures are non-fatal (cache serves
    // the current render) but should still be traced.
    fetchAndCacheThumbs(businessKey).catch(err => {
      console.warn('[imageService] background thumbnail refresh failed:', err?.message)
      Sentry.captureException(err, { extra: { module: 'imageService', fn: 'fetchAndCacheThumbs.background', businessKey } })
    })
    return cached
  }
  return fetchAndCacheThumbs(businessKey)
}

async function fetchAndCacheThumbs(businessKey) {
  const map = new Map()
  const q = query(collection(db, COLLECTION), where('businessKey', '==', businessKey))
  const snap = await getDocsFromServer(q)
  console.log(`[Images] Fetched ${snap.size} product image docs for ${businessKey}`)
  snap.forEach(d => {
    const data = d.data()
    const images = data.images || []
    if (images.length === 0) return
    map.set(data.itemId, [images[0].url])
  })
  setCachedThumbs(businessKey, map)
  return map
}

// ─── Alias for backward compatibility (Admin page) ───
export async function getAllBusinessImages(businessKey) {
  const map = new Map()
  const q = query(collection(db, COLLECTION), where('businessKey', '==', businessKey))
  const snap = await getDocsFromServer(q)
  snap.forEach(d => {
    const data = d.data()
    const urls = (data.images || []).map(img => img.url)
    if (urls.length > 0) map.set(data.itemId, urls)
  })
  return map
}

// ─── Public API: get full images for ONE product (on-demand, for detail modal) ───

export async function getFullImages(businessKey, itemId) {
  const id = docId(businessKey, itemId)
  const docRef = doc(db, COLLECTION, id)
  const snap = await getDoc(docRef)
  if (!snap.exists()) return []
  return (snap.data().images || []).map(img => img.url)
}
